import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "@phosphor-icons/react";
import { API } from "../../App";

// Shared cache to avoid refetching across all SourceSelect instances on the page
let _cache = null;
const _subs = new Set();
async function fetchSources(force = false) {
  if (_cache && !force) return _cache;
  try {
    const r = await axios.get(`${API}/leads/sources`, { params: { with_usage: false } });
    _cache = r.data || [];
    _subs.forEach((cb) => cb(_cache));
    return _cache;
  } catch {
    return [];
  }
}

export default function SourceSelect({
  value, onChange, user, mode = "form", className = "", testId = "lead-source-select", disabled = false,
}) {
  const [sources, setSources] = useState(_cache || []);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const isFilter = mode === "filter";
  const canQuickAdd = !isFilter && ["admin", "md"].includes((user?.role || "").toLowerCase());

  useEffect(() => {
    const cb = (rows) => setSources(rows);
    _subs.add(cb);
    fetchSources();
    return () => { _subs.delete(cb); };
  }, []);

  // Always include the current value as an option even if it's legacy / deactivated,
  // so editing an old lead keeps its source visible.
  const optionRows = (() => {
    const active = sources.filter((s) => s.is_active && !s.is_legacy);
    const v = (value || "").trim();
    if (!v) return active;
    const present = active.some((s) => s.slug === v || s.name === v);
    if (present) return active;
    // Try find it as legacy/inactive
    const legacy = sources.find((s) => s.slug === v || s.name === v);
    if (legacy) return [...active, { ...legacy, _legacy_hint: true }];
    // Truly unknown — pass through
    return [...active, { source_id: "free", slug: v, name: v, _legacy_hint: true }];
  })();

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <select
        data-testid={testId}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        disabled={disabled}
        className="rounded-none w-full bg-white border border-slate-300 focus:border-[#002FA7] p-2 text-sm outline-none"
      >
        {isFilter && <option value="">All sources</option>}
        {!isFilter && <option value="">— Select source —</option>}
        {optionRows.map((s) => (
          <option key={s.slug + (s._legacy_hint ? "_l" : "")} value={s.slug}>
            {s._legacy_hint ? `${s.name} (legacy)` : s.name}
          </option>
        ))}
      </select>
      {canQuickAdd && (
        <button
          type="button"
          data-testid={`${testId}-quick-add`}
          onClick={() => setQuickAddOpen(true)}
          className="p-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10"
          title="Quick add source"
        >
          <Plus size={14} weight="bold" />
        </button>
      )}
      {quickAddOpen && (
        <QuickAddSource
          user={user}
          onClose={() => setQuickAddOpen(false)}
          onCreated={(row) => {
            setQuickAddOpen(false);
            fetchSources(true).then(() => onChange && onChange(row.slug));
          }}
        />
      )}
    </div>
  );
}

function QuickAddSource({ user, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/leads/sources`, {
        name, description, is_active: active,
        requester_user_id: user?.user_id, requester_name: user?.name, requester_role: user?.role,
      });
      toast.success(`Source “${r.data.name}” ready`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" data-testid="quick-add-source-modal">
      <div className="bg-white border border-slate-200 max-w-md w-full">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-bold">Quick Add Lead Source</h3>
          <button onClick={onClose} data-testid="quick-add-source-close" className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Source Name *</label>
            <input data-testid="quick-add-source-name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 outline-none mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Description</label>
            <textarea data-testid="quick-add-source-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 outline-none mt-1 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="quick-add-source-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="quick-add-source-submit" onClick={submit} disabled={busy}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300">
            {busy ? "Saving…" : "Create Source"}
          </button>
        </div>
      </div>
    </div>
  );
}
